import React, { Fragment, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEqual, isPlainObject, pick, find, isEmpty, uniq, partition } from 'lodash-es';
import { useDebounce } from '../../hooks';

import {
  Menu,
  SubMenu,
  MenuItem,
  MenuGroup,
  MenuHeader,
  MenuDivider,
  MenuSpacer,
  MenuButton,
  ShowOnlySelected,
  CustomLabelSwitch,
  CustomLabelCheckbox,
  SelectableSubMenuLabel,
  MenuItemGhostButton,
  RequestAccess,
  useMenuProps,
  useSubMenuProps,
  useSearchOnMenuOpen,
  useShadowLine,
  filterBySearchQuery,
} from '../DropdownSet';
import Tooltip from '../Tooltip';
import SearchField from '../Search/SearchField';

import {
  excludeInactiveCountries,
  prepareRegionsList,
  excludeWorldwide,
  getSelectedRegions,
  unpackRegions,
  packRegions,
} from './config';
import { makeCountriesOptions } from '../../utils/helpers';
import usStates, { isoStates } from '../../utils/usa_states';

const emptyArray = [];

const GeoFilterDropdown = ({
  selected,
  selectedStates = emptyArray,
  includedUnknownHQ = false,
  regionsList,
  countryOptionsWithStatus,
  enableWorldwide = true,
  enableUnknownHQ = false,
  enableUSStates,
  allowRegionSlug,
  allowUnselectedState = false,
  partialAccess = false,
  requestFeature,
  debounceTime = 700,
  // Appearance
  size,
  caret = true,
  frameless = false,
  shape,
  variant,
  position,
  menuAlign,
  disabled,
  staticButtonLabel,
  // Handlers
  makeRegionTooltip,
  decorateButtonLabel,
  onChange,
  onDebounceChange,
}) => {
  const splitByActive = !isEmpty(countryOptionsWithStatus);

  const [activeCountryOptions, inactiveCountryOptions] = useMemo(
    () => partition(countryOptionsWithStatus, 'active'),
    [countryOptionsWithStatus]
  );

  const memoRegionsList = useMemo(
    () =>
      splitByActive ? excludeInactiveCountries(regionsList, inactiveCountryOptions) : regionsList,
    [regionsList, inactiveCountryOptions]
  );

  const { flattenRegionsList, countriesIsoByRegion, allCountriesOfRegions, regionsOptions } =
    useMemo(
      () =>
        prepareRegionsList(memoRegionsList, {
          enableWorldwide,
          partialAccess,
        }),
      [memoRegionsList]
    );

  const allRegionsValues = useMemo(
    () => flattenRegionsList.map((region) => region.value).filter((value) => value !== 'worldwide'),
    [flattenRegionsList]
  );

  const allCountriesIso = useMemo(() => {
    if (splitByActive) {
      const activeCountriesIso = activeCountryOptions.map(({ value }) => value);
      return uniq([...allCountriesOfRegions, ...activeCountriesIso]);
    }

    return allCountriesOfRegions;
  }, [activeCountryOptions]);

  const menuInstanceRef = useRef();
  const menuRef = useRef();
  const isMenuOpenRef = useRef(false);
  const { searchInstanceRef, onMenuChange: onSearchMenuChange } = useSearchOnMenuOpen();
  const lastItemWarningTimeoutRef = useRef();

  const selectedISOs = useMemo(
    () => unpackRegions(selected, countriesIsoByRegion),
    [selected, countriesIsoByRegion]
  );

  const [localState, setLocalState] = useState({
    selected: selectedISOs,
    includedUnknownHQ,
    selectedStates,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showSelected, setShowSelected] = useState(false);
  const [showLastItemWarning, setShowLastItemWarning] = useState(false);

  const debouncedState = useDebounce(localState, debounceTime);

  const menuProps = useMenuProps({
    ...(position ? { position } : {}),
    ...(menuAlign ? { align: menuAlign } : {}),
    menuStyle: { width: 282 },
    className: 'country-filter-dropdown',
  });

  const subMenuProps = useSubMenuProps({
    menuStyle: { width: 260 },
  });

  const [shadowLine, onScroll] = useShadowLine();

  const isSearchActive = searchQuery.trim().length > 0;
  const isWWChosen = localState.selected.includes('WW');
  const isWWOFFChosen = localState.selected.includes('WWOFF'); // DEVnote: we need this flag to be able to deselect WW visually without losing all selected countries
  const isUSChosen = localState.selected.includes('US');

  const normalizedSelected = enableWorldwide && isWWChosen ? allCountriesIso : localState.selected;

  const normalizeSelectedStates =
    enableUSStates && isUSChosen && !localState.selectedStates.length
      ? isoStates
      : localState.selectedStates;

  // Regions
  const { selectedRegions } = useMemo(
    () => getSelectedRegions(localState.selected, { countriesIsoByRegion, flattenRegionsList }),
    [localState.selected, countriesIsoByRegion, flattenRegionsList]
  );

  const filteredRegionsOptions = useMemo(
    () => filterBySearchQuery(regionsOptions, searchQuery),
    [searchQuery, regionsOptions]
  );

  // Countries
  const countriesOptions = useMemo(
    () => makeCountriesOptions(allCountriesIso, { enableUSStates }),
    [allCountriesIso, enableUSStates]
  );

  const filteredCountriesOptions = useMemo(() => {
    const options = countriesOptions.reduce((result, country) => {
      if (showSelected && !normalizedSelected.includes(country.value)) return result;

      return result.concat({
        ...country,
        disabled: partialAccess && !localState.selected.includes(country.value),
      });
    }, []);

    return filterBySearchQuery(options, searchQuery);
  }, [searchQuery, countriesOptions, showSelected, normalizedSelected]);

  const filteredInactiveCountriesOptions = useMemo(
    () => filterBySearchQuery(inactiveCountryOptions, searchQuery),
    [searchQuery, inactiveCountryOptions]
  );

  const showDivider = filteredRegionsOptions.length > 0 && filteredCountriesOptions.length > 0;

  const handleUnknownHQInclusion = useCallback((e) => {
    e.keepOpen = true;
    setLocalState((prevState) => ({ ...prevState, includedUnknownHQ: e.checked }));
  }, []);

  // Regions selection
  const handleRegionsSelection = (e) => {
    e.keepOpen = true;

    // WW is "select all" shortcut — deselect visually by selecting WWOFF without changing selected countries in localState,
    if (e.value === 'worldwide' && !e.checked) {
      setLocalState((prevState) => ({
        ...prevState,
        selected: [...prevState.selected, 'WWOFF'],
        selectedStates: [],
      }));
      return;
    }
    const isAllRegions = isEqual(allRegionsValues, [...selectedRegions, e.value]);

    let nextCountries;
    if (e.checked) {
      if (e.value === 'worldwide') {
        nextCountries = countriesIsoByRegion[e.value];
      } else {
        const nextSelectedCountries = uniq([
          ...localState.selected,
          ...countriesIsoByRegion[e.value],
        ]);
        nextCountries =
          enableWorldwide && (isWWChosen || nextSelectedCountries.length === allCountriesIso.length)
            ? ['WW']
            : nextSelectedCountries;
      }
    } else if (isWWChosen) {
      nextCountries = selectedRegions.reduce((acc, region) => {
        if (region !== e.value) acc.push(...countriesIsoByRegion[region]);
        return acc;
      }, []);
    } else {
      nextCountries =
        isAllRegions && enableWorldwide
          ? ['WW']
          : localState.selected.filter(
              (country) => !countriesIsoByRegion[e.value].includes(country)
            );
    }

    setLocalState((prevState) => ({
      ...prevState,
      selected: nextCountries,
      selectedStates: [],
    }));
  };

  const handleOnlyRegionSelection = (region) => {
    setLocalState((prevState) => ({
      ...prevState,
      selected: countriesIsoByRegion[region],
      selectedStates: [],
    }));
    // Set focus on menu to be able to close outside
    menuRef.current.focus();
  };

  // Countries selection
  const handleCountriesSelection = useCallback(
    (e) => {
      e.keepOpen = true;

      setLocalState((prevState) => {
        let nextCountries;

        if (e.checked) {
          nextCountries = [...excludeWorldwide(prevState.selected), e.value];

          // If all countries are selected then replace with WW
          const allCountriesSelected = nextCountries.length === allCountriesIso.length;
          if (enableWorldwide && !inactiveCountryOptions.length && allCountriesSelected) {
            nextCountries = ['WW'];
          }
        } else {
          nextCountries = (
            prevState.selected.includes('WW') ? allCountriesIso : prevState.selected
          ).filter((country) => country !== e.value);
        }
        return { ...prevState, selected: nextCountries };
      });
    },
    [allCountriesIso]
  );

  const handleOnlyCountrySelection = (countryIso) => {
    setLocalState((prevState) => ({
      ...prevState,
      selected: [countryIso],
      selectedStates: [],
    }));
    // Set focus on menu to be able to close outside
    menuRef.current.focus();
  };

  // States selection
  const handleSelectedStatesChange = (states) => {
    const withoutUSCountries = normalizedSelected.filter((countryIso) => countryIso !== 'US');
    const nextCountries = states.length ? [...withoutUSCountries, 'US'] : withoutUSCountries;
    const isFullUS = states.length === isoStates.length;

    setLocalState((prevState) => ({
      ...prevState,
      selected: nextCountries,
      selectedStates: isFullUS ? [] : states,
    }));
  };

  const handleStatesSelection = (e) => {
    e.keepOpen = true;

    const nextStates = e.checked
      ? [...localState.selectedStates, e.value]
      : normalizeSelectedStates.filter((usState) => usState !== e.value);

    handleSelectedStatesChange(nextStates);
  };

  const handleStateSelection = (value) => {
    setLocalState((prevState) => ({ ...prevState, selected: ['US'], selectedStates: [value] }));
    // Set focus on menu to be able to close outside
    menuRef.current.focus();
  };

  const toggleShowSelected = useCallback((e) => {
    e.keepOpen = true;
    setShowSelected(e.checked);
  }, []);

  const handleApply = (nextSelectedState, nextOnChange) => {
    const isApplyDisabled = !allowUnselectedState && !nextSelectedState.selected?.length;

    if (isApplyDisabled) return;

    const { selectedRegions: nextSelectedRegions } = getSelectedRegions(
      nextSelectedState.selected,
      { countriesIsoByRegion, flattenRegionsList }
    );

    let nextSelected = nextSelectedState.selected;

    if (allowRegionSlug) {
      const isAllRegionsSelected = isEqual(allRegionsValues, nextSelectedRegions);
      nextSelected =
        isAllRegionsSelected && enableWorldwide
          ? ['WW']
          : packRegions(nextSelectedState.selected, nextSelectedRegions, countriesIsoByRegion);
    }

    nextOnChange(nextSelected, pick(nextSelectedState, ['includedUnknownHQ', 'selectedStates']));
  };

  const handleMenuChange = ({ open }) => {
    isMenuOpenRef.current = open;
    // Cleanup search field on toggle Menu
    onSearchMenuChange({ open });
  };

  const handleShowLastWarning = useCallback((e) => {
    e.keepOpen = true;
    clearTimeout(lastItemWarningTimeoutRef.current);
    setShowLastItemWarning(true);
    lastItemWarningTimeoutRef.current = setTimeout(() => setShowLastItemWarning(false), 3000);
  }, []);

  // Effects
  useEffect(() => {
    if (!isMenuOpenRef.current) {
      setLocalState((prevState) => {
        const nextState = { selected: selectedISOs, includedUnknownHQ, selectedStates };
        if (isEqual(prevState, nextState)) return prevState;
        return nextState;
      });
    }
  }, [selectedISOs, includedUnknownHQ, selectedStates]);

  useEffect(() => {
    if (isMenuOpenRef.current && onChange && !localState.selected.includes('WWOFF')) {
      handleApply(localState, onChange);
    }
  }, [localState]);

  useEffect(() => {
    if (isMenuOpenRef.current && onDebounceChange && !localState.selected.includes('WWOFF')) {
      handleApply(debouncedState, onDebounceChange);
    }
  }, [debouncedState]);

  const forbiddenLastItemDeselect =
    !allowUnselectedState &&
    (normalizedSelected.length === 1 || (selectedRegions.length === 1 && !isWWChosen));

  const activeRegionsCount = useMemo(
    () =>
      flattenRegionsList.filter((region) => {
        if (region.value === 'worldwide') return false;
        const regionCountries = countriesIsoByRegion[region.value] || [];
        return regionCountries.some((iso) => normalizedSelected.includes(iso));
      }).length,
    [flattenRegionsList, countriesIsoByRegion, normalizedSelected]
  );

  const renderRegionMenuItem = (region) => {
    const { label, value, disabled: disabledOption } = region;
    const isWorldwide = value === 'worldwide';
    const isChecked =
      selectedRegions.includes(value) || (isWorldwide && isWWChosen && !isWWOFFChosen);
    const CustomLabel = isWorldwide ? CustomLabelSwitch : CustomLabelCheckbox;
    const regionCountries = countriesIsoByRegion[value] || [];
    const selectedCountInRegion = regionCountries.filter((iso) =>
      normalizedSelected.includes(iso)
    ).length;
    const isIndeterminate =
      !isWorldwide && selectedCountInRegion > 0 && selectedCountInRegion < regionCountries.length;

    const isOnlyActiveRegion = (isChecked || isIndeterminate) && activeRegionsCount === 1;
    const showOnlyRegionButton = !(value === 'worldwide' || isOnlyActiveRegion);
    const isRegionDisabled =
      !isWorldwide && !allowUnselectedState && isChecked && !isIndeterminate && isOnlyActiveRegion;

    return (
      <Tooltip
        key={value}
        content={makeRegionTooltip ? makeRegionTooltip(region) : null}
        placement="left"
        overlayStyle={{ maxWidth: 202 }}
      >
        <MenuItem
          value={value}
          checked={isChecked}
          type="checkbox"
          disabled={disabledOption}
          className={cx({ 'ghost-button-item': showOnlyRegionButton })}
          onClick={isRegionDisabled ? handleShowLastWarning : handleRegionsSelection}
        >
          <CustomLabel
            id={value}
            checked={isChecked}
            disabled={disabledOption}
            labelText={label}
            {...(!isWorldwide && { indeterminate: isIndeterminate })}
          />
          {showOnlyRegionButton && (
            <MenuItemGhostButton value={value} onClick={handleOnlyRegionSelection} />
          )}
        </MenuItem>
      </Tooltip>
    );
  };

  const buttonLabel = useMemo(() => {
    if (staticButtonLabel) return staticButtonLabel;

    if (!selectedISOs.length) return 'Select Country';

    if (
      selectedRegions.length === 1 &&
      countriesIsoByRegion[selectedRegions[0]].length === selectedISOs.length
    ) {
      const [selectedRegion] = selectedRegions;
      return find(flattenRegionsList, ['value', selectedRegion]).text;
    }

    // Single country selected
    if (selectedISOs.length === 1) {
      if (selectedStates.length === 1) {
        const [selectedState] = selectedStates;
        return (find(usStates, ['value', selectedState]) || {}).label;
      }

      if (selectedStates.length > 1) return 'Multiple Locations';

      const [selectedCountry] = selectedISOs;
      if (selectedCountry === 'WW') return 'Worldwide';

      return (find(countriesOptions, ['value', selectedCountry]) || {}).label;
    }

    return 'Multiple Countries';
  }, [
    selectedISOs,
    countriesOptions,
    countriesIsoByRegion,
    flattenRegionsList,
    selectedRegions,
    selectedStates,
    staticButtonLabel,
  ]);

  const requestFeatureParams = useMemo(() => {
    if (isPlainObject(requestFeature)) return requestFeature;

    return {
      featureName: requestFeature,
      title: 'Access All Countries',
      description: 'Gain great insight with access to all countries and regions.',
    };
  }, [requestFeature]);

  return (
    <Menu
      menuButton={
        <MenuButton
          size={size}
          caret={caret}
          shape={shape}
          variant={variant}
          disabled={disabled}
          frameless={frameless}
        >
          {decorateButtonLabel ? decorateButtonLabel(buttonLabel) : buttonLabel}
        </MenuButton>
      }
      setDownOverflow
      instanceRef={menuInstanceRef}
      ref={menuRef}
      {...menuProps}
      onMenuChange={handleMenuChange}
    >
      <SearchField instanceRef={searchInstanceRef} onChange={setSearchQuery} />
      {shadowLine}
      <MenuGroup takeOverflow onScroll={onScroll}>
        {enableUnknownHQ && (
          <MenuItem
            type="checkbox"
            checked={localState.includedUnknownHQ}
            onClick={handleUnknownHQInclusion}
          >
            <CustomLabelCheckbox
              id="include-unknown-hq"
              checked={localState.includedUnknownHQ || false}
              labelText="Include Unknown HQ"
            />
          </MenuItem>
        )}

        {showDivider && <MenuHeader>Regions</MenuHeader>}

        {filteredRegionsOptions.map(renderRegionMenuItem)}

        {showDivider && <MenuDivider />}
        {showDivider && <MenuHeader>Countries</MenuHeader>}

        {!isSearchActive && (
          <ShowOnlySelected checked={showSelected} onClick={toggleShowSelected} />
        )}
        {filteredCountriesOptions.map((country) => {
          const isCheckedCountry = normalizedSelected.includes(country.value);
          const isOnlyOneCountryChecked = normalizedSelected.length === 1 && isCheckedCountry;
          const showOnlyCountryButton = !isOnlyOneCountryChecked;
          const isCountryDisabled = isOnlyOneCountryChecked && !allowUnselectedState;

          if (enableUSStates && country.children) {
            return (
              <SubMenu
                key={country.value}
                label={
                  <Fragment>
                    <SelectableSubMenuLabel
                      label={country.label}
                      selected={normalizeSelectedStates}
                      subMenus={isoStates}
                      disabled={country.disabled}
                      onChange={handleSelectedStatesChange}
                    />
                    {showOnlyCountryButton && (
                      <MenuItemGhostButton
                        value={country.value}
                        onClick={handleOnlyCountrySelection}
                      />
                    )}
                  </Fragment>
                }
                disabled={country.disabled}
                {...subMenuProps}
              >
                {country.children.map(({ label, value }) => {
                  const isCheckedState = normalizeSelectedStates.includes(value);
                  const isOnlyOneStateChecked =
                    isOnlyOneCountryChecked && isUSChosen && localState.selectedStates.length === 1;
                  const showOnlyStateButton = !(isCheckedState && isOnlyOneStateChecked);

                  return (
                    <MenuItem
                      key={value}
                      type="checkbox"
                      value={value}
                      checked={isCheckedState}
                      className={cx({ 'ghost-button-item': showOnlyStateButton })}
                      onClick={handleStatesSelection}
                    >
                      <CustomLabelCheckbox id={value} checked={isCheckedState} labelText={label} />
                      {showOnlyStateButton && (
                        <MenuItemGhostButton value={value} onClick={handleStateSelection} />
                      )}
                    </MenuItem>
                  );
                })}
              </SubMenu>
            );
          }

          return (
            <MenuItem
              key={country.value}
              type="checkbox"
              value={country.value}
              checked={isCheckedCountry}
              disabled={country.disabled}
              className={cx({ 'ghost-button-item': showOnlyCountryButton })}
              onClick={isCountryDisabled ? handleShowLastWarning : handleCountriesSelection}
            >
              <CustomLabelCheckbox
                id={country.value}
                checked={isCheckedCountry}
                disabled={country.disabled}
                labelText={country.label}
              />
              {showOnlyCountryButton && (
                <MenuItemGhostButton value={country.value} onClick={handleOnlyCountrySelection} />
              )}
            </MenuItem>
          );
        })}

        {!showSelected && filteredInactiveCountriesOptions.length > 0 && (
          <Fragment>
            <MenuDivider />
            {filteredInactiveCountriesOptions.map((country) => (
              <Tooltip
                key={country.value}
                content={country.tooltip || 'No data available for this country'}
                placement="left"
                overlayStyle={{ maxWidth: 202 }}
              >
                <MenuItem type="checkbox" value={country.value} checked={false} disabled>
                  <CustomLabelCheckbox
                    id={country.value}
                    checked={false}
                    disabled
                    labelText={country.label}
                  />
                </MenuItem>
              </Tooltip>
            ))}
          </Fragment>
        )}
        <MenuSpacer />
      </MenuGroup>
      {forbiddenLastItemDeselect && showLastItemWarning && (
        <div className="country-filter-dropdown__warning menu-bottom-warning">
          <div className="menu-bottom-warning__text">At least one selection required.</div>
        </div>
      )}
      {partialAccess && (
        <Fragment>
          <MenuDivider />
          <RequestAccess
            featureName={requestFeatureParams.featureName}
            title={requestFeatureParams.title}
            description={requestFeatureParams.description}
          />
        </Fragment>
      )}
    </Menu>
  );
};

GeoFilterDropdown.propTypes = {
  selected: T.array.isRequired,
  selectedStates: T.array,
  includedUnknownHQ: T.bool,
  regionsList: T.array.isRequired,
  countryOptionsWithStatus: T.arrayOf(
    T.shape({
      label: T.string,
      value: T.string,
      active: T.bool,
    })
  ),
  enableWorldwide: T.bool,
  enableUnknownHQ: T.bool,
  enableUSStates: T.bool,
  allowUnselectedState: T.bool,
  allowRegionSlug: T.bool,
  partialAccess: T.bool,
  requestFeature: T.oneOfType([
    T.string,
    T.shape({
      featureName: T.string,
      title: T.string,
      description: T.string,
    }),
  ]),
  debounceTime: T.number,
  // Appearance
  size: T.oneOf(['lg']),
  caret: T.bool,
  frameless: T.bool,
  shape: T.string,
  variant: T.string,
  position: T.string,
  menuAlign: T.string,
  disabled: T.bool,
  staticButtonLabel: T.node,
  // Handlers
  decorateButtonLabel: T.func,
  makeRegionTooltip: T.func,
  onChange: T.func,
  onDebounceChange: T.func,
};

export default GeoFilterDropdown;
